import { PROJECT_INSTRUCTIONS, SESSION_PROMPTS } from './prompts';

// Static fallback schedule. The production app reads `sessions` from Supabase
// and falls back to this if the table is empty or the network is offline.
// Times are local to America/Los_Angeles (event venue).
//
// Day indices: 0 = Wed May 20 (arrival), 1 = Thu May 21 (Day 1 of 2),
//              2 = Fri May 22 (Day 2 of 2).
//
// Row types:
//   - 'session'  → full card. kind ∈ { 'session', 'joint', 'flex' } drives styling.
//   - 'break'    → lighter divider row (meals, breaks, transitions, transport)
//   - 'note'     → lighter divider row used for the arrival-day informal items

export const SCHEDULE = {
  // ── WED MAY 20 — Arrival Day (no formal agenda) ────────────────────────
  0: [
    { type: 'note', time: 'Afternoon', label: 'Travel & hotel check-in' },
    { type: 'note', time: 'Evening',   label: 'Informal gathering (optional)' },
  ],

  // ── THU MAY 21 — Day 1 of 2 ────────────────────────────────────────────
  1: [
    { type: 'break', time: '6:30 AM', label: 'Breakfast at the hotel' },
    { type: 'break', time: '7:30 AM', label: 'Depart for venue' },

    {
      type: 'session', kind: 'flex',
      id: 'welcome', num: 'Welcome', title: 'Welcome by President, Dr. Michael Ramcharan',
      time: '8:00 – 8:05 AM', duration: '5 min', day: 1,
      startsAt: '2026-05-21T08:00:00-07:00',
      endsAt:   '2026-05-21T08:05:00-07:00',
      desc:
        '📍 Room M-71. Opening welcome from Dr. Michael Ramcharan, President of Southern California University of Health Sciences.',
      prompt: null,
    },

    {
      type: 'session', kind: 'session',
      id: 's0', num: 'Session 0', title: 'AI Project Setup',
      time: '8:05 – 8:30 AM', duration: '25 min', day: 1,
      startsAt: '2026-05-21T08:05:00-07:00',
      endsAt:   '2026-05-21T08:30:00-07:00',
      desc:
        '📍 Room M-71. All laptops open. Each ED builds their association AI project using a guided prompt in Claude or ChatGPT. Facilitator models live on screen. Foundation for all sessions that follow.',
      promptLabel: 'PROJECT INSTRUCTIONS',
      prompt: PROJECT_INSTRUCTIONS,
    },

    {
      type: 'session', kind: 'session',
      id: 's1', num: 'Session 1', title: 'Succession Planning: ED & Staff Continuity',
      time: '8:30 – 10:15 AM', duration: '1 hr 45 min', day: 1,
      startsAt: '2026-05-21T08:30:00-07:00',
      endsAt:   '2026-05-21T10:15:00-07:00',
      desc:
        '📍 Room M-71. Discussion: passwords and admin access, institutional knowledge documentation, key-person insurance, what day one looks like for a successor, how to capture what you do every day. AI workshop: build a succession framework and gap analysis specific to your association.',
      prompt: SESSION_PROMPTS.s1,
    },

    { type: 'break', time: '10:15 – 10:30 AM', label: 'Break' },

    {
      type: 'session', kind: 'session',
      id: 's2', num: 'Session 2',
      title: 'Governance, Board Effectiveness & Leadership Pipeline',
      time: '10:30 – 11:45 AM', duration: '1 hr 15 min', day: 1,
      startsAt: '2026-05-21T10:30:00-07:00',
      endsAt:   '2026-05-21T11:45:00-07:00',
      desc:
        '📍 Room M-71. Discussion: board structure, size, bylaws, communication norms, ethics policies, strategic plan as governance filter, board succession, leadership development pipeline. AI workshop: map your board structure, identify pipeline gaps, draft an onboarding outline for incoming leaders.',
      prompt: SESSION_PROMPTS.s2,
    },

    { type: 'break', time: '11:45 AM – 12:00 PM', label: 'Transition to Student Lunch' },

    {
      type: 'session', kind: 'joint',
      id: 'lunch1', num: 'Joint Session', title: 'Student Meet & Greet Lunch',
      time: '12:00 – 1:15 PM', duration: '1 hr 15 min', day: 1,
      startsAt: '2026-05-21T12:00:00-07:00',
      endsAt:   '2026-05-21T13:15:00-07:00',
      desc:
        '📍 The Glen (Outdoors). Joint session with chiropractic college EDs. Share who you are, your state, and a few facts about practice in your state. Bring business cards and leave-behind materials.',
      prompt: null,
    },

    {
      type: 'session', kind: 'session',
      id: 's3', num: 'Session 3',
      title: 'Membership: Recruiting, Onboarding & Communicating Value',
      time: '1:30 – 3:15 PM', duration: '1 hr 45 min', day: 1,
      startsAt: '2026-05-21T13:30:00-07:00',
      endsAt:   '2026-05-21T15:15:00-07:00',
      desc:
        '📍 Room M-71. Discussion: what is working, what is not, articulating ROI, member (including student) engagement strategies, onboarding sequences. This session gets the most time because it generates the most discussion. AI workshop: build or audit your onboarding flow and draft a membership value statement.',
      prompt: SESSION_PROMPTS.s3,
    },

    {
      type: 'session', kind: 'flex',
      id: 'tour', num: 'Campus Tour', title: 'Campus Tour',
      time: '3:15 – 4:00 PM', duration: '45 min', day: 1,
      startsAt: '2026-05-21T15:15:00-07:00',
      endsAt:   '2026-05-21T16:00:00-07:00',
      desc:
        '📍 SCUHS campus. Guided 45-minute walking tour of the campus.',
      prompt: null,
    },

    {
      type: 'session', kind: 'flex',
      id: 'flex1', num: 'Open Discussion', title: 'Overflow & Flex Time',
      time: '4:00 – 5:00 PM', duration: '1 hr', day: 1,
      startsAt: '2026-05-21T16:00:00-07:00',
      endsAt:   '2026-05-21T17:00:00-07:00',
      desc:
        '📍 Room M-71. Reserved for topics that ran long, threads worth continuing, or organic conversation. May preview Friday sessions.',
      prompt: null,
    },

    {
      type: 'session', kind: 'joint',
      id: 'dinner1', num: 'Group Dinner', title: 'Group Dinner at Yard House',
      time: '5:30 – 7:30 PM', duration: '2 hr', day: 1,
      startsAt: '2026-05-21T17:30:00-07:00',
      endsAt:   '2026-05-21T19:30:00-07:00',
      desc:
        '📍 Yard House — 160 S Brea Blvd, Brea, CA 92821 (about 15 min from SCUHS). Group reservation. Casual dinner together to close out the first day.',
      prompt: null,
    },
  ],

  // ── FRI MAY 22 — Day 2 of 2 ────────────────────────────────────────────
  2: [
    { type: 'break', time: '6:30 AM', label: 'Breakfast at the hotel' },
    { type: 'break', time: '7:30 AM', label: 'Depart for venue' },

    {
      type: 'session', kind: 'session',
      id: 's4', num: 'Session 4',
      title: 'Communication Strategy: Staying Relevant & Trusted in an AI-Saturated World',
      time: '8:00 – 9:45 AM', duration: '1 hr 45 min', day: 2,
      startsAt: '2026-05-22T08:00:00-07:00',
      endsAt:   '2026-05-22T09:45:00-07:00',
      desc:
        '📍 Room M-71. Discussion: how associations stay visible and credible when members can find information anywhere. Positioning as the trusted authority, content strategy, and using AI to produce consistent communication without losing your voice. AI workshop: build a communication strategy for your association.',
      prompt: SESSION_PROMPTS.s4,
    },

    { type: 'break', time: '9:45 – 10:00 AM', label: 'Break' },

    {
      type: 'session', kind: 'session',
      id: 's5', num: 'Session 5',
      title: 'Revenue Diversification: Non-Dues Revenue & Financial Sustainability',
      time: '10:00 – 11:45 AM', duration: '1 hr 45 min', day: 2,
      startsAt: '2026-05-22T10:00:00-07:00',
      endsAt:   '2026-05-22T11:45:00-07:00',
      desc:
        '📍 Room M-71. Most associations rely too heavily on dues. This session is about changing that. Discussion: building and growing non-dues revenue streams including CEU models, sponsorship strategy, advertising, and other income sources. AI workshop: analyze your current revenue mix and build a plan to pursue the top two or three.',
      prompt: SESSION_PROMPTS.s5,
    },

    {
      type: 'session', kind: 'session',
      id: 'closing', num: 'Closing', title: 'Commitments & Takeaways',
      time: '11:45 AM – 12:00 PM', duration: '15 min', day: 2,
      startsAt: '2026-05-22T11:45:00-07:00',
      endsAt:   '2026-05-22T12:00:00-07:00',
      desc:
        '📍 Room M-71. Each attendee states one thing they are doing when they get home and by when. Keeps the event from ending on inspiration and ensures it ends on action.',
      prompt: null,
    },

    { type: 'break', time: '12:00 PM', label: 'Adjourn — safe travels' },
  ],
};

export function flattenSessions(s = SCHEDULE) {
  return [...(s[0] || []), ...(s[1] || []), ...(s[2] || [])].filter(
    (i) => i.type === 'session',
  );
}
